import React, { useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Box, Typography, TextField, IconButton,
    Divider
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { formatDate } from '../util/date';
import Dropdown, { DropdownOption } from './ui/dropdown';
import { DatePicker } from '@mui/x-date-pickers';

import ActionMenu from './action-menu';
import Toggle from './ui/toggle';
import dayjs from 'dayjs';
import TablePlaceholder from './table-placeholder';
import { ChevronDown, ChevronUp } from 'lucide-react';

export type Column = {
    id: string;
    label: string;
    size?: number;
    type?: 'string' | 'number' | 'date' | 'dropdown' | 'toggle' | 'actions' | 'checkbox' | 'currency' | 'multi-toggle';
    disabled?: boolean;
    options?: DropdownOption[];
    buttons?: { label: string; onClick: (row: Record<string, any>) => void }[];
    onToggle?: (row: Record<string, any>, value: boolean) => void;
    onSort?: (column: string) => void;
    onEdit?: (row: Record<string, any>) => void;
    editLabel?: string;
    onDelete?: (row: Record<string, any>) => void;
    deleteLabel?: string;
    sibling?: string;
    toggleLabels?: { on: string; off: string; };
}

type AdditionalAction = {
    label: string;
    icon?: 'edit' | 'delete' | 'add' | 'info' | 'download' | 'upload' | 'close' | 'save' | 'warning' | 'check' | 'user' | 'eye' | 'invite';
    // Gets the row the menu was opened on.
    onClick: (row: Record<string, any>) => void;
}

type DynamicTableProps = {
    columns: Column[];
    data: Record<string, any>[];
    additionalActions?: AdditionalAction[];
    pagination?: React.ReactNode;
};

const DynamicAdminTable: React.FC<DynamicTableProps> = ({ columns, data, additionalActions, pagination }) => {
    const [sort, setSort] = useState<{ column: string; direction: 'ASC' | 'DESC' }>({ column: '', direction: 'ASC' });
    const [editData, setEditData] = useState<Record<string, any>[]>(data);
    const [actionAnchorEls, setActionAnchorEls] = useState<(null | HTMLElement)[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    useEffect(() => {
        setEditData(data);
        setActionAnchorEls(new Array(data.length).fill(null));
        setSelectedRows([]);
    }, [data]);

    const isAllSelected = editData.length > 0 && selectedRows.length === editData.length;
    const isIndeterminate = selectedRows.length > 0 && selectedRows.length < editData.length;
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedRows(editData.map((_, idx) => idx));
        } else {
            setSelectedRows([]);
        }
    };
    const handleSelectRow = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedRows(prev =>
            e.target.checked ? [...prev, idx] : prev.filter(i => i !== idx)
        );
    };

    const handleChange = (rowIdx: number, colId: string, value: any) => {
        setEditData(prev => prev.map((row, idx) =>
            idx === rowIdx ? { ...row, [colId]: value } : row
        ));
    };

    const handleActionClick = (rowIdx: number, event: React.MouseEvent<HTMLElement>) => {
        setActionAnchorEls(prev => {
            const copy = [...prev];
            copy[rowIdx] = event.currentTarget;
            return copy;
        });
    };
    const handleActionClose = (rowIdx: number) => {
        setActionAnchorEls(prev => {
            const copy = [...prev];
            copy[rowIdx] = null;
            return copy;
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TableContainer
                component={Paper}
                sx={{
                    p: 0,
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    border: '2px solid #c2c2c2ff',
                    background: 'var(--color-page-background)',
                }}
            >
                <Table sx={{ minWidth: 650, background: 'var(--color-page-background)' }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((col, colIdx) => {
                                // Render multi-toggle header with colSpan=2
                                if (
                                    col.type === 'multi-toggle' &&
                                    col.sibling &&
                                    columns[colIdx + 1]?.id === col.sibling
                                ) {
                                    return (
                                        <TableCell
                                            key={col.id}
                                            sx={{
                                                fontWeight: 600,
                                                color: 'var(--color-text-muted)',
                                                fontSize: 15,
                                                width: col.size,
                                                cursor: col.onSort ? 'pointer' : undefined,
                                                userSelect: 'none',
                                                borderBottom: '1px solid #c9c9c9ff',
                                                px: 2,
                                                py: 1.5,
                                                textAlign: 'center',
                                            }}
                                            onClick={col.onSort ? () => {
                                                const newDirection = sort.column === col.id && sort.direction === 'ASC' ? 'DESC' : 'ASC';
                                                setSort({ column: col.id, direction: newDirection });
                                                col.onSort && col.onSort(newDirection);
                                            } : undefined}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                                                <Typography fontWeight={600} color="var(--color-text-muted)" fontSize={15}>
                                                    {[col, columns.find(c => c.id === col.sibling)]
                                                        .filter(Boolean)
                                                        .map((toggleCol, i, arr) => (
                                                            <React.Fragment key={toggleCol!.id}>
                                                                {toggleCol!.label}
                                                                {i < arr.length - 1 && '/'}
                                                            </React.Fragment>
                                                        ))
                                                    }
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    );
                                }
                                // Skip the sibling column for multi-toggle
                                if (
                                    col.type === 'multi-toggle' &&
                                    colIdx > 0 &&
                                    columns[colIdx - 1]?.sibling === col.id
                                ) {
                                    return null;
                                }
                                // Default rendering for other column types
                                return (
                                    <TableCell
                                        key={col.id}
                                        sx={{
                                            fontWeight: 600,
                                            color: 'var(--color-text-muted)',
                                            fontSize: 15,
                                            width: col.size,
                                            cursor: col.onSort ? 'pointer' : undefined,
                                            userSelect: 'none',
                                            borderBottom: '1px solid #c9c9c9ff',
                                            px: 2,
                                            py: 1.5,
                                            textAlign: col.type === 'actions' || col.type === 'currency' ? 'right' : col.type === 'toggle' ? 'center' : 'left',
                                        }}
                                        onClick={col.onSort ? () => {
                                            const newDirection = sort.column === col.id && sort.direction === 'ASC' ? 'DESC' : 'ASC';
                                            setSort({ column: col.id, direction: newDirection });
                                            col.onSort && col.onSort(newDirection);
                                        } : undefined}
                                    >
                                        <Box 
                                            display="flex" 
                                            alignItems="center" 
                                            gap={2}
                                            justifyContent={col.type === 'actions' || col.type === 'currency' ? 'flex-end' : col.type === 'toggle' ? 'center' : 'flex-start'}
                                        >
                                            <Typography fontWeight={600} color="var(--color-text-muted)">{col.label}</Typography>
                                            {col.onSort && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.5, color: 'var(--color-primary)', p: 0 }}
                                                    tabIndex={-1}
                                                    aria-label="Sort"
                                                >
                                                    {sort.column === col.id ? (
                                                        sort.direction === 'ASC'
                                                            ? <ChevronUp size={20} color='var(--color-text-muted)'/>
                                                            : <ChevronDown size={20} color='var(--color-text-muted)'/>
                                                    ) : (
                                                        <ChevronUp size={20} color='#C2C2C2'/>
                                                    )}
                                                </IconButton>
                                            )}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {editData?.length > 0 ? editData.map((row, idx) => (
                            <TableRow
                                key={idx}
                                sx={{
                                    height: 56,
                                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                                }}
                            >
                                {columns.map((col, colIdx) => {
                                    if (
                                        col.type === 'multi-toggle' &&
                                        col.sibling &&
                                        columns[colIdx + 1]?.id === col.sibling
                                    ) {
                                        return (
                                            <TableCell
                                                key={col.id}
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    fontSize: 14,
                                                    borderBottom: '1px solid #e5e5e5ff',
                                                    color: 'var(--color-default-text)',
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle',
                                                }}
                                            >
                                                <Box display="flex" flexDirection="column" gap={0.5}>
                                                    {[col, columns.find(c => c.id === col.sibling)].filter(Boolean).map((toggleCol, toggleIdx) => (
                                                        <Box key={toggleCol!.id} display="flex" alignItems="center" gap={1}>
                                                            <Toggle
                                                                key={toggleCol!.id}
                                                                checked={row[toggleCol!.id] ?? false}
                                                                onChange={val => {
                                                                    handleChange(idx, toggleCol!.id, val);
                                                                    if (toggleCol!.onToggle) {
                                                                        toggleCol!.onToggle(row, val);
                                                                    }
                                                                }}
                                                            />
                                                            <Typography fontSize={13} color="var(--color-text-muted)" whiteSpace="nowrap">
                                                                {toggleCol!.toggleLabels
                                                                    ? (row[toggleCol!.id] ? toggleCol!.toggleLabels.on : toggleCol!.toggleLabels.off)
                                                                    : (row[toggleCol!.id] ? 'On' : 'Off')}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </TableCell>
                                        );
                                    }
                                    // Skip the sibling column for multi-toggle
                                    if (
                                        col.type === 'multi-toggle' &&
                                        colIdx > 0 &&
                                        columns[colIdx - 1]?.sibling === col.id
                                    ) {
                                        return null;
                                    }
                                    // Default rendering for other column types
                                    return (
                                        <TableCell
                                            key={col.id}
                                            sx={{
                                                px: 2,
                                                py: 1.5,
                                                fontSize: 14,
                                                borderBottom: '1px solid #e5e5e5ff',
                                                color: 'var(--color-default-text)',
                                                textAlign: col.type === 'actions' || col.type === 'currency' ? 'right' : col.type === 'toggle' ? 'center' : 'left',
                                                verticalAlign: 'middle',
                                            }}
                                        >
                                            {col.type === 'checkbox' && colIdx === 0 ? (
                                                <Checkbox
                                                    checked={selectedRows.includes(idx)}
                                                    onChange={handleSelectRow(idx)}
                                                    sx={{ width: 18, height: 18, p: 0.5 }}
                                                />
                                            ) : col.type === 'actions' ? (
                                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                                    <ActionMenu
                                                        actions={[
                                                            ...(col.onEdit ? [{
                                                                label: col.editLabel || 'Edit',
                                                                icon: 'edit',
                                                                onClick: () => col.onEdit && col.onEdit(row)
                                                            }] : []),
                                                            ...(additionalActions?.map(action => ({
                                                                label: action.label,
                                                                icon: action.icon,
                                                                onClick: () => action.onClick(row)
                                                            })) || []),
                                                            ...(col.onDelete ? [{
                                                                label: col.deleteLabel || 'Delete',
                                                                icon: 'delete',
                                                                onClick: () => col.onDelete && col.onDelete(row)
                                                            }] : []),
                                                        ]}
                                                    />
                                                </Box>
                                            ) : col.type ? (
                                                col.type === 'dropdown' ? (
                                                    <Dropdown
                                                        options={col.options || []}
                                                        value={row[col.id] ?? ''}
                                                        onChange={val => handleChange(idx, col.id, val)}
                                                        placeholder="Select..."
                                                        isPrimary={true}
                                                        height={24}
                                                    />
                                                ) : col.type === 'date' ? (
                                                    <DatePicker
                                                        value={row[col.id] ? dayjs(row[col.id]) : null}
                                                        onChange={val => handleChange(idx, col.id, val ? formatDate(val.toString()) : '')}
                                                        slotProps={{ textField: { fullWidth: true, size: 'small', variant: 'outlined' } }}
                                                    />
                                                ) : col.type === 'toggle' ? (
                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                        <Toggle
                                                            checked={row[col.id]}
                                                            onChange={val => {
                                                                handleChange(idx, col.id, val);
                                                                col.onToggle && col.onToggle(row, val);
                                                            }}
                                                        />
                                                    </Box>
                                                ) : col.disabled ? (
                                                    (col.id === 'createdAt' || col.id === 'updatedAt') && !col.type
                                                        ? formatDate(row[col.id])
                                                        : row[col.id]
                                                ) : (
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        type={col.type === 'number' ? 'number' : 'text'}
                                                        value={row[col.id] ?? ''}
                                                        onChange={e => handleChange(idx, col.id, col.type === 'number' ? Number(e.target.value) : e.target.value)}
                                                    />
                                                )
                                            ) : (
                                                col.id === 'createdAt' || col.id === 'updatedAt'
                                                    ? formatDate(row[col.id])
                                                    : row[col.id]
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ py: 6 }}>
                                    <TablePlaceholder
                                        title="No Data Available"
                                        message="No data available for the selected filters."
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {pagination && (
                    <Box px={2} py={1} display="flex" justifyContent="flex-end" alignItems="center" borderTop="1.5px solid #c2c2c2ff" bgcolor="var(--color-page-background)">
                        {pagination}
                    </Box>
                )}
            </TableContainer>
        </LocalizationProvider>
    );
};

export default DynamicAdminTable;
